import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { UIError } from 'common/interfaces';
import {
  IDashboardDataType,
  IDashboardErrorStatusType,
  IDashboardLoadingType,
  IDashboardState,
  IDashboardStats,
  IReportSalesStats,
  IReportStats,
  IStatsSearchCriteria,
} from 'common/interfaces/idashboard';
import { merge } from 'lodash';

const initialState: IDashboardState = {
  loading: false,
  error: null,
  loadingStatus: undefined,
  errorStatus: undefined,
  currentStats: undefined,
  previousStats: undefined,
  searchCriteria: undefined,
  currentSalesStats: undefined,
  previousSalesStats: undefined,
  currentGasSalesStats: undefined,
  previousGasSalesStats: undefined,
} as IDashboardState;

const reducers = {
  setLoading: (state: IDashboardState, action: PayloadAction<boolean>) => {
    state.loading = action.payload;
  },
  setDataLoading: (state: IDashboardState, action: PayloadAction<{ type: IDashboardDataType; isLoading: boolean }>) => {
    if (!state.loadingStatus) {
      state.loadingStatus = {} as IDashboardLoadingType;
    }
    state.loadingStatus[action.payload.type] = action.payload.isLoading;
  },
  setDataError: (state: IDashboardState, action: PayloadAction<{ type: IDashboardDataType; hasError: boolean }>) => {
    if (!state.errorStatus) {
      state.errorStatus = {} as IDashboardErrorStatusType;
    }
    state.errorStatus[action.payload.type] = action.payload.hasError;
  },
  setError: (state: IDashboardState, action: PayloadAction<UIError | null>) => {
    state.error = action.payload;
  },
  resetContext: (state: IDashboardState) => {
    merge(state, initialState);
    // `merge` skips undefined source values, so the per-section flags would
    // otherwise survive a reset and leave stale errors on screen.
    state.errorStatus = undefined;
  },
  setCurrentStats: (state: IDashboardState, action: PayloadAction<IDashboardStats | undefined>) => {
    state.currentStats = action.payload;
  },
  setPreviousStats: (state: IDashboardState, action: PayloadAction<IDashboardStats | undefined>) => {
    state.previousStats = action.payload;
  },
  setSearchCriteria: (state: IDashboardState, action: PayloadAction<IStatsSearchCriteria | undefined>) => {
    state.searchCriteria = action.payload;
  },
  setCurrentSalesStats: (state: IDashboardState, action: PayloadAction<IReportSalesStats[] | undefined>) => {
    state.currentSalesStats = action.payload;
  },
  setPreviousSalesStats: (state: IDashboardState, action: PayloadAction<IReportSalesStats[] | undefined>) => {
    state.previousSalesStats = action.payload;
  },

  setCurrentGasSalesStats: (state: IDashboardState, action: PayloadAction<IReportSalesStats[] | undefined>) => {
    state.currentGasSalesStats = action.payload;
  },
  setPreviousGasSalesStats: (state: IDashboardState, action: PayloadAction<IReportSalesStats[] | undefined>) => {
    state.previousGasSalesStats = action.payload;
  },

  setCurrentSalesStatsStoreWise: (state: IDashboardState, action: PayloadAction<IReportSalesStats[] | undefined>) => {
    state.currentSalesStatsStoreWise = action.payload;
  },
  setPreviousSalesStatsStoreWise: (state: IDashboardState, action: PayloadAction<IReportSalesStats[] | undefined>) => {
    state.previousSalesStatsStoreWise = action.payload;
  },

  setCurrentGasSalesStatsStoreWise: (
    state: IDashboardState,
    action: PayloadAction<IReportSalesStats[] | undefined>,
  ) => {
    state.currentGasSalesStatsStoreWise = action.payload;
  },
  setPreviousGasSalesStatsStoreWise: (
    state: IDashboardState,
    action: PayloadAction<IReportSalesStats[] | undefined>,
  ) => {
    state.previousGasSalesStatsStoreWise = action.payload;
  },
};

const dashboardSlice = createSlice({
  name: 'IDashboardState',
  initialState: initialState,
  reducers: reducers,
});

export default dashboardSlice.reducer;
export const DashboardActions = dashboardSlice.actions;
